/* eslint-disable max-len */
/** @jsx jsx */
import FormContents from "components/atoms/Form/FormContents";
import { css, jsx } from "@emotion/core";
import BlockUI from "components/molecules/BlockUi";
import BodyText from 'components/atoms/BodyText';
import FlexBox from 'components/atoms/FlexBox';
import FlexBoxItem from 'components/atoms/FlexBoxItem';
import { grayScale, productColor } from "components/styles";
import SelectForm from "components/molecules/SelectForm";
import FormField from "components/atoms/Form/FormField";
import FormLayout from "components/atoms/Form/VerticalLabelFormLayout";
import FormLabel from "components/atoms/Form/FormLabel";
import YearPickerForm from 'components/molecules/YearPickerForm';
import HourInput from 'components/atoms/Form/HourInput';
import ListWarningThreshold from './ListWarningThreshold';
import PrimaryButton from 'components/atoms/Button';
import useMasterSettingAgreement36Form from './hooks';
import Modal from 'components/molecules/Modal';
import FormHeader from 'components/templates/FormHeader';

const styles = {
	formContainer: css({
		width: "70%",
		marginLeft: "15px",
		border: "0",
	}),
	formLabel: css({
		paddingTop: '8px', 
		width: '250px'
	}),
	formNote: css({
		margin: '0 0 2px 16px', 
		color: '#333333'
	}),
	formInput: css({
		width: '65px', 
		height: '40px'
	}),
	errorNote: css({
		color: productColor.secondary
	}),
	warningNote: css({
		color: productColor.copyAction
	}),
	formTitle: css({
		margin: '50px 0 20px'
	}),
}

const MasterSettingAgreement36Form = () => {
	const {
		isLoading,
		targetYear,
		setTargetYear,
		listMonths,
		monthOptions,
		formik,
		openModal,
		setOpenModal,
		isEdit,
		setPreviousYearData
	} = useMasterSettingAgreement36Form();

	const getMonthSelectValue = (date: Date|null) => {
		let monthValue = (date ? date.getMonth() : 0) + 1;
		return monthValue > 10 ? String(monthValue) : `0${monthValue}`
	}

	return (
		<BlockUI blocking={isLoading}>
			<FormHeader title="36協定アラート設定" />
			<FormContents customStyle={css({marginTop:'60px'})}>
				<form css={styles.formContainer}>
					<FormField>
						<FormLayout
							label={<FormLabel label="起算日" customStyle={css({marginTop: '7px'})}/>}
							input={
								<FlexBox>
									<div style={{ width: "65px" }}>
										<SelectForm
											label=""
											name="month"
											value={getMonthSelectValue(formik.values.applyStartMonth)}
											options={monthOptions}
											setValue={(val: string) => {
												formik.setFieldValue('applyStartMonth', new Date(targetYear, Number(val) - 1, 1))
											}}
											description=""
											width="65px"
										/>
									</div>
									<div style={{ marginLeft: "12px" }}>
										<BodyText color={grayScale.gray60}>月</BodyText>
									</div>
								</FlexBox>
							}
						/>
					</FormField>
					<FormField>
						<YearPickerForm
							name="year"
							label="対象年度"
							selectedYear={new Date().getFullYear()}
							onChange={(year: any) => {
												 setTargetYear(year);
												 // formik.setFieldValue('targetYear', year)
											 }}
							addChild={
								<PrimaryButton
									text="前年の内容を取り込む"
									onClick={() => setPreviousYearData()}
									ghost={true}
								/>
							}
						/>										
					</FormField>
					<FormLabel label="月次時間外労働時間アラート" customStyle={styles.formTitle}/>
					<ListWarningThreshold 
						listWarningThreshold={formik.values.monthlyOvertimeHourList}
						setListWarningThreshold={
							(monthlyOvertimeHourList: Array<number>) => formik.setFieldValue('monthlyOvertimeHourList', monthlyOvertimeHourList)
						}
						label="当月の時間外労働時間が"
						noteInput="時間を超える残業件数を表示する"
						maxLength={4}
						maxLengthInput={2}
					/>
					<FormLabel label="年次時間外労働時間アラート" customStyle={styles.formTitle}/>
					<ListWarningThreshold 
						listWarningThreshold={formik.values.yearOvertimeHourList}
						setListWarningThreshold={
							(yearOvertimeHourList: Array<number>) => formik.setFieldValue('yearOvertimeHourList', yearOvertimeHourList)
						}
						label="当年の時間外労働時間が"
						noteInput="時間を超える残業件数を表示する"
						maxLength={4}
						maxLengthInput={3}
					/>
					<FormField customStyle={css({marginTop: '40px'})}>
						<FormLabel customStyle={styles.formLabel} label="2~6ヶ月の時間外労働平均時間が"/>
						<FlexBox>
							<HourInput
								name={'averageOvertimeHour'}
								value={String(formik.values.averageOvertimeHour)}
								label=""
								onChange={(e) => { 
									formik.setFieldValue('averageOvertimeHour', Number(e.target.value))
								}}
								min={0}
								max={744}
								widthInput="50px"
								wrapInput={styles.formInput}
							/>
							<FormLabel customStyle={css([styles.formNote,{ color: productColor.secondary }])} label="時間を超える残業をアラートとする"/>
						</FlexBox>
					</FormField>
					<FormField customStyle={css({marginTop: '40px'})}>
						<FormLabel customStyle={styles.formLabel} label="当月の時間外と休日の労働時間合計が"/>
						<FlexBox>
							<HourInput
								name={'averageHours'}
								value={String(formik.values.monthlyOverAndHolidayTimeHour)}
								label=""
								onChange={(e) => { 
									formik.setFieldValue('monthlyOverAndHolidayTimeHour', Number(e.target.value))
								}}
								min={1}
								max={80}
								widthInput="50px"
								wrapInput={styles.formInput}
							/>
							<FormLabel customStyle={css([styles.formNote,{ color: productColor.secondary }])} label="時間を超える残業をアラートとする"/>
						</FlexBox>
					</FormField>
					<FormLabel label="月の時間外労働時間超過回数アラート" customStyle={css({marginTop: '50px'})}/>
					<FormField>
						<FormLabel customStyle={styles.formLabel} label="時間外労働届出の上限"/>
						<FlexBox customStyle={css({height: 'fit-content', marginRight: '20px'})}>
							<HourInput
								name={'hourOvertimeMonthly'}
								value={String(formik.values.monthOverConditionsHour)}
								label=""
								onChange={(e) => { 
									formik.setFieldValue('monthOverConditionsHour', Number(e.target.value))
								}}
								min={0}
								widthInput="50px"
								wrapInput={styles.formInput}
							/>
							<FormLabel customStyle={styles.formNote} label="時間を"/>
						</FlexBox>
						<FlexBoxItem>
							<ListWarningThreshold 
								listWarningThreshold={formik.values.monthlyOverConditionsCountList}
								setListWarningThreshold={
									(monthlyOverConditionsCountList: Array<number>) => formik.setFieldValue('monthlyOverConditionsCountList', monthlyOverConditionsCountList)
								}
								label=""
								noteInput="回を超える残業回数を表示する"
								maxLength={4}
								maxLengthInput={1}
							/>
						</FlexBoxItem>
					</FormField>
				</form>
				<FormField justifyContent="center" customStyle={css({marginBottom: '10px'})}>
					<PrimaryButton
						text={isEdit ? "更新" : "登録"}
						onClick={() => setOpenModal(true)}
					/>
				</FormField>
			</FormContents>
			<Modal
				open={openModal}
				closeHandler={() => setOpenModal(false)}
				title={isEdit ? "36協定アラート設定の更新" : "36協定アラート設定の登録"}
				note={isEdit ? "36協定アラート設定マスタを更新します。よろしいですか？" : "36協定アラート設定マスタを登録します。よろしいですか？"}
				submitText={isEdit ? "更新" : "登録"}
				onSubmit={() => formik.handleSubmit()}
			></Modal>
		</BlockUI>
	);
};

export default MasterSettingAgreement36Form;
